import dayjs from "dayjs";
import type { DownloadEvent } from "@tauri-apps/plugin-updater";
import { app } from "@tauri-apps/api";
import { LocalStorageState } from "@mixitone/components";
import tauri from "lib/tauri";

/** Emitted from Rust during `download_and_install_update` (see main.rs). */
export const MIXITONE_UPDATER_PROGRESS_EVENT = "mixitone-updater-progress";

export type MixitoneUpdaterProgressPayload = {
  downloaded: number;
  total: number | null;
};

export type UpdateManifestResult = {
  shouldUpdate: boolean;
  manifest: {
    version: string;
    date: string | null;
    body: string | null;
  };
};

export async function resolveUpdateChannel(): Promise<"beta" | "release"> {
  const updateChannelState = new LocalStorageState<"beta" | "release">("updateChannel", "release");
  const storedChannel = updateChannelState.get();

  // Always honor explicit user preference first so settings survive refreshes.
  if (storedChannel === "beta" || storedChannel === "release") {
    return storedChannel;
  }

  if (!tauri.enabled) {
    return "release";
  }
  try {
    const detectedChannel = (await tauri.getUpdateChannel()) as "beta" | "release";
    const version = await app.getVersion();
    if (/beta|alpha|rc/i.test(version)) {
      return "beta";
    }
    return detectedChannel;
  } catch {
    return "release";
  }
}

export function isChannelSwitch(currentVersion: string, remoteVersion: string): boolean {
  return (
    (currentVersion.includes("beta") && !remoteVersion.includes("beta")) ||
    (!currentVersion.includes("beta") && remoteVersion.includes("beta"))
  );
}

/** Matches OfflineNotification: snooze hides toast and skips background download. */
export function isUpdateRemindLaterActive(): boolean {
  const remindAtFromStorage = localStorage.getItem("remindAt");
  if (!remindAtFromStorage) return false;
  return dayjs().isBefore(dayjs(remindAtFromStorage));
}

/** Dev-only: `?debugUpdate=1` exercises the toast without contacting release servers. */
export function isDebugUpdateUiEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return new URLSearchParams(window.location.search).get("debugUpdate") === "1";
  } catch {
    return false;
  }
}

/** Build/runtime escape hatch for portable debug binaries. */
export function isUpdaterDisabled(): boolean {
  return import.meta.env.VITE_DISABLE_UPDATER === "1";
}

/** Whether to contact release servers / hotswap (skipped in Vite dev unless ?debugUpdate=1). */
export function shouldCheckForUpdates(): boolean {
  if (isUpdaterDisabled()) return false;
  if (import.meta.env.DEV && !isDebugUpdateUiEnabled()) return false;
  return true;
}

export type DownloadProgressTracker = {
  bytesDownloaded: number;
  bytesTotal: number;
};

/** Maps plugin updater download events to 0–100 for display. */
export function applyDownloadEvent(
  tracker: DownloadProgressTracker | undefined,
  event: DownloadEvent,
): { tracker: DownloadProgressTracker | undefined; percent: number } {
  switch (event.event) {
    case "Started":
      if (event.data.contentLength) {
        return {
          tracker: { bytesDownloaded: 0, bytesTotal: event.data.contentLength },
          percent: 0,
        };
      }
      return { tracker: undefined, percent: 0 };
    case "Progress":
      if (tracker) {
        const next = {
          ...tracker,
          bytesDownloaded: tracker.bytesDownloaded + event.data.chunkLength,
        };
        if (next.bytesTotal > 0) {
          return {
            tracker: next,
            percent: Math.min(100, Math.round((next.bytesDownloaded / next.bytesTotal) * 100)),
          };
        }
      }
      return { tracker, percent: 0 };
    case "Finished":
      return { tracker, percent: 100 };
    default:
      return { tracker, percent: 0 };
  }
}
